import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { detectarIdioma, traducir } from '../i18n/idioma';
import { useIdioma } from '../i18n/idioma';
import { fetchAllWorkspaces } from '../services/workspaceUsers';
import { notificarLecturaDenegada } from '../services/sesionPerdida';
import { esLanding } from '../utils/rutas';

import { useAuth } from './AuthContext';

// ============================================================
//  WORKSPACE PROFILES
//  Workspaces are now fetched dynamically from the `workspaces`
//  table in Supabase. The user's allowed_workspaces in 
//  allowed_users determines which ones they can see.
//
//  Fallback profiles are kept for offline/initial render.
// ============================================================

export interface WorkspaceProfile {
    id: string;
    name: string;
    shortName: string;        // Shown in compact UI slots
    accentColor: string;      // Optional branding per workspace
    voiceEnabled: boolean;    // Per-workspace gate for cloned-voice AI (defaults to false until super admin flips it)
    voiceDailyCapChars: number; // Daily ElevenLabs character budget (default 5000)
    /**
     * Correo de quien es dueño del negocio. Se traía en la fila del taller
     * desde siempre y este mapeo lo tiraba, así que la app no tenía forma de
     * distinguir al dueño de un Admin invitado: los dos salían como «Admin»
     * en la lista de gente.
     *
     * Solo sirve para ETIQUETAR y para esconder los botones de editar/quitar
     * en su fila. Quien de verdad manda es el guard de la base de datos
     * (`_member_guard_violation`), que ancla en esta misma columna; esconder un
     * botón no protege nada por sí solo.
     *
     * Cadena vacía cuando no se pudo resolver — nunca `undefined`, para que
     * una comparación descuidada no convierta «no se sabe» en «sí es».
     */
    ownerEmail: string;
}

// Fallback profiles used before DB fetch completes
const FALLBACK_PROFILES: WorkspaceProfile[] = [
    {
        id: 'default',
        name: traducir(detectarIdioma(), 'empresa.nombrePorDefecto'),
        shortName: traducir(detectarIdioma(), 'empresa.cortoPorDefecto'),
        accentColor: '#FACC15',
        voiceEnabled: false,
        voiceDailyCapChars: 5000,
        ownerEmail: '',
    }
];

// Keep exported for backward compatibility
export const WORKSPACE_PROFILES = FALLBACK_PROFILES;

// ============================================================
//  CONTEXT
// ============================================================

// Explicit resolution state so consumers can tell an unresolved/errored
// workspace fetch apart from a genuinely empty one.
export type WorkspaceStatus = 'loading' | 'resolved' | 'error';

interface WorkspaceContextType {
    activeWorkspace: WorkspaceProfile;
    setActiveWorkspaceById: (id: string) => void;
    workspaces: WorkspaceProfile[];
    // Kept for existing consumers; derived from `status`.
    loading: boolean;
    // Reads/writes must gate on `status === 'resolved'` — never settle CRM
    // scoping on the 'default' fallback when the fetch failed or returned empty.
    status: WorkspaceStatus;
    // Re-attempt the workspace fetch (wired to the "reintentar" error UI).
    retry: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

const STORAGE_KEY = '2bros_active_workspace';

export const WorkspaceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { t } = useIdioma();
    const { isAuthorized, loading: authLoading } = useAuth();
    const [workspaces, setWorkspaces] = useState<WorkspaceProfile[]>(FALLBACK_PROFILES);
    const [status, setStatus] = useState<WorkspaceStatus>('loading');
    const [activeWorkspace, setActiveWorkspace] = useState<WorkspaceProfile>(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const found = FALLBACK_PROFILES.find(p => p.id === stored);
                if (found) return found;
                // Preserve the stored ID so we don't overwrite it before fetchWorkspaces succeeds
                return {
                    id: stored,
                    name: t('espacio.cargando'),
                    shortName: '...',
                    accentColor: '#6366f1',
                    voiceEnabled: false,
                    voiceDailyCapChars: 5000,
                    // Aún no se sabe quién es el dueño: cadena vacía, que
                    // `esDuenoDelTaller` trata como «nadie». Mientras el fetch
                    // no llega, nadie sale etiquetado como dueño — es el lado
                    // seguro del error.
                    ownerEmail: '',
                };
            }
        } catch { /* ignore */ }
        return FALLBACK_PROFILES[0];
    });

    // Fetch workspaces from Supabase with a bounded retry. An EMPTY list (no
    // error) is treated as UNRESOLVED, not a valid empty state — otherwise CRM
    // queries would scope to the 'default' fallback and legitimately return zero
    // rows, making the user's own data look deleted.
    const loadWorkspaces = useCallback(async () => {
        setStatus('loading');
        const MAX_ATTEMPTS = 3;
        let lastError: unknown = null;

        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
            try {
                const { data, error } = await fetchAllWorkspaces();
                if (!error && data && data.length > 0) {
                    const profiles: WorkspaceProfile[] = data.map((ws: any) => ({
                        id: ws.id,
                        name: ws.name,
                        shortName: ws.short_name || ws.name,
                        accentColor: ws.accent_color || '#FACC15',
                        voiceEnabled: Boolean(ws.voice_enabled),
                        voiceDailyCapChars: typeof ws.voice_daily_cap_chars === 'number' ? ws.voice_daily_cap_chars : 5000,
                        ownerEmail: (ws.owner_email || '').trim().toLowerCase(),
                    }));
                    setWorkspaces(profiles);

                    // Keep the stored selection if it is still valid; otherwise
                    // fall back to the first workspace the user can access.
                    let storedId: string | null = null;
                    try { storedId = localStorage.getItem(STORAGE_KEY); } catch { /* ignore */ }
                    const found = profiles.find(p => p.id === storedId);
                    setActiveWorkspace(found || profiles[0]);
                    setStatus('resolved');
                    return;
                }
                // No error but no rows -> unresolved; record and retry.
                lastError = error || new Error('No workspaces returned');
            } catch (err) {
                lastError = err;
            }

            // Linear backoff between attempts (skip the wait after the last one).
            if (attempt < MAX_ATTEMPTS) {
                await new Promise(res => setTimeout(res, attempt * 500));
            }
        }

        console.warn('[WorkspaceContext] Could not resolve workspaces after retries:', lastError);
        // Una lista vacía o denegada con la sesión «viva» es la firma de una
        // sesión perdida por debajo de la app: RLS filtra todas las filas para
        // anon y esto es lo primero —a veces lo único— que lo nota, porque con
        // el taller sin resolver ninguna consulta llega a dispararse. Que
        // AuthContext lo mire (services/sesionPerdida): con token guardado no
        // hará nada, así que un taller sin miembros no cierra ninguna sesión.
        notificarLecturaDenegada(
            'workspaces',
            lastError instanceof Error ? lastError.message : String((lastError as { message?: unknown })?.message ?? lastError),
        );
        setStatus('error');
    }, []);

    const { pathname } = useLocation();
    const enLanding = esLanding(pathname);

    useEffect(() => {
        if (authLoading) return; // Wait until AuthContext determines session state
        if (!isAuthorized) {
            // Auth resolved but no session/authorization: there is no workspace to
            // resolve. Mark unresolved (this also clears `loading`) so the app
            // never hangs, and write guards keep refusing the 'default' fallback.
            console.log('[WorkspaceContext] Not authorized yet, using fallback.');
            setStatus('error');
            return;
        }
        if (enLanding) {
            // La página de marketing no necesita taller, y con sesión tampoco
            // debe cargar la capa de datos: todos los proveedores cuelgan por
            // encima del router, así que alguien con la app aparcada en «/»
            // —días, en el caso del registro— tiraba de todas las consultas en
            // cada vuelta de foco. Sin resolver, cada `useTallerListo` queda en
            // `false` y no se pide nada. Al entrar en la app, el efecto vuelve a
            // correr con la ruta nueva y se resuelve como siempre.
            setStatus('loading');
            return;
        }
        loadWorkspaces();
    }, [isAuthorized, authLoading, enLanding, loadWorkspaces]);

    // Persist selection — but NEVER write the 'default' fallback. Persisting it
    // would pin CRM scoping to 'default' after a storage eviction. Wrapped in
    // try/catch because private-mode / evicted storage throws on write.
    useEffect(() => {
        if (activeWorkspace.id === 'default') return;
        try {
            localStorage.setItem(STORAGE_KEY, activeWorkspace.id);
        } catch { /* private-mode storage throws; ignore */ }
    }, [activeWorkspace]);

    const setActiveWorkspaceById = (id: string) => {
        const found = workspaces.find(p => p.id === id);
        if (found) setActiveWorkspace(found);
    };

    const retry = useCallback(() => {
        loadWorkspaces();
    }, [loadWorkspaces]);

    const loading = status === 'loading';

    return (
        <WorkspaceContext.Provider value={{ activeWorkspace, setActiveWorkspaceById, workspaces, loading, status, retry }}>
            {children}
        </WorkspaceContext.Provider>
    );
};

export const useWorkspace = (): WorkspaceContextType => {
    const ctx = useContext(WorkspaceContext);
    if (!ctx) throw new Error('useWorkspace must be used within a WorkspaceProvider');
    return ctx;
};
